import React from "react";

import { Box, Stack, Typography, Grid, Skeleton, useTheme } from "@mui/material";
// Direct path, not the `components` barrel: InfoBlock is itself re-exported from
// that barrel, so importing through it would be a cycle.
import { InfoTooltip, InfoGlyph } from "../../feedback/InfoTooltip";
// One character, one source, read by both apps — see the constant's own docstring
// for why an absent value renders a dash rather than a blank.
import { EMPTY_VALUE } from "@repo/ui";

export const InfoRow = ({
  children,
  columns,
}: {
  children: React.ReactNode;
  /**
   * Opt in to a FIXED N-column grid instead of the default `space-between` flex row.
   *
   * The default divides the row evenly among however many items it holds, so a
   * 5-item row sits its fields at 0/20/40/60/80% while the 3-item row beneath it
   * sits them at 0/33/66% — nothing lines up vertically, and a panel of several
   * rows reads as ragged rather than as a table. That is fine for the many
   * one- and two-row blocks in this app, which is why it stays the default and
   * every existing call site is untouched.
   *
   * With `columns`, each row lays its items into the SAME column tracks, so a
   * short row simply leaves its trailing cells empty and its items still align
   * with the row above. That is what makes a long, multi-row panel scan.
   *
   * `minmax(0, 1fr)` rather than a bare `1fr`: `1fr` is really `minmax(auto, 1fr)`,
   * whose `auto` floor is the content's min-content width, so one long unbroken
   * value (a support email, a subdomain) would push the track wider than its share
   * and blow the grid out of alignment — the exact failure this prop exists to fix.
   */
  columns?: number;
}) =>
  columns ? (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
      }}
    >
      {children}
    </Box>
  ) : (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      {children}
    </Stack>
  );

export const InfoBlock = ({
  title,
  children,
  sx,
}: {
  title?: string;
  children: React.ReactNode;
  sx?: object;
}) => {
  const theme = useTheme();
  return (
    <>
      <Grid
        sx={{
          // Mode-aware surface + border, matching the shared Card. It used
          // `primary[50]` for BOTH fill and border, and that scale is not mode-aware —
          // it is #f6f6f6 in either mode. In dark that reads as a raised panel against
          // the near-black page; in light it is the page colour, so the block had no
          // surface at all and only the row dividers suggested one existed.
          //
          // background.paper and grey[100] flip with the mode, so the panel reads the
          // same way in both, and it is now the same treatment as every Card on the
          // page rather than a second, hand-rolled one.
          background: theme.palette.background.paper,
          borderRadius: 3,
          border: `1px solid ${theme.palette.grey[100]}`,
          ...sx,
        }}
      >
        {/*
          INSIDE the panel, not floating above it.

          It rendered above as a `body1` with `pb: 3`, and that was wrong in a way
          nothing caught because NOTHING USED IT: 18 `<InfoBlock>` call sites across
          the platform, every one of them untitled, so `title` was dead code until
          the product page's "Certificate defaults" became the first caller. The
          result was a titled card sitting beside an untitled one, with the heading
          detached from the surface it names — Spencer, on the product page: "this
          looks odd because Certificate defaults is above the card while the other
          cards have no title".

          `body2` at weight 500 is not a new treatment either: it is exactly what
          the Default-tag card on that same page already uses for its own in-card
          heading, so a titled InfoBlock now reads as a sibling of the cards around
          it rather than as a different kind of thing.

          `pb: 0` deliberately — the first row's own `py: 4` supplies the gap, so a
          padding here would double it.
        */}
        {title && (
          <Typography
            /*
              `body1` (20px), NOT `body2`. Measured on the product page at
              1280x900: as `body2` this heading rendered at the SAME 16px as every
              field LABEL and every field VALUE in the grid beneath it, separated
              only by weight 500 against 400 — one step, which is not enough to
              read as a tier. Spencer: "looks the same size as field title".

              20px is also what the prop ALREADY used before it moved inside the
              panel (`variant="body1"`). Only the position was wrong; shrinking it
              to match the in-card "Default tag" heading threw away the size that
              made it a heading. That one gets away with 16px because it is a
              standalone card with a sentence under it, not a heading over a grid
              of 16px labels — a different problem with a different answer.

              Weight 500 on top of the size step, so the tier holds in both themes
              without leaning on colour.
            */
            sx={{
              /*
                18px / 600, MEASURED off Settings' own card heading ("Personal" on
                /settings/profile) rather than chosen — Spencer: "how do we do it
                elsewhere on the platform? maybe like we do it on settings - bold
                it?". That heading is 18px/600 at 87% ink over 14px/400 labels.

                It was `body1` 20px/500. Size alone was carrying the whole tier,
                and one weight step is not enough to stop a heading reading as
                another field label — which is what "conflicts with the card field
                titles" was. 600 does the separating here, so the size step can be
                smaller and the heading stops competing for the eye.

                A literal px, not a variant: no token in this theme is 18px
                (body1 20 / body2 16 / body3 14), and matching the reference
                exactly matters more than spending a token that does not fit.

                ⚠️ KNOWN RESIDUAL, not fixed here. Settings pairs its 18px heading
                with 14px field labels; InfoItem's label and value are BOTH 16px,
                so this card is a denser wall of text than the reference and the
                heading clears it by 2px rather than 4. Dropping InfoItem to 14
                would match Settings properly, but it moves every one of the 18
                InfoBlocks on the platform and is not what was reported.
              */
              fontSize: '18px',
              fontWeight: 600,
              lineHeight: '24px',
              px: 6,
              pt: 4,
              /*
                `pb: 6` (24px), not 0. The earlier note claimed the first row's own
                `py: 4` supplied the gap — it supplied 16px, and the card's OWN
                rhythm between a value and the next label measured 38px. So the
                heading sat CLOSER to the first field than the fields sat to each
                other, which is backwards: the section break should be the largest
                gap in the card, not the smallest. 24 + the row's 16 lands it at
                the card's own spacing rather than under it.
              */
              pb: 6,
              width: '100%',
            }}
          >
            {title}
          </Typography>
        )}
        {children}
      </Grid>
    </>
  );
};

export const InfoItem = ({
  label,
  value,
  term,
  tooltip = false,
  sx,
  spacing = true,
  valueSpacing = 2,
}: {
  label: string;
  value: React.ReactNode;
  /**
   * A glossary entry id. The tooltip body is then that term's `short`, read from
   * `docs/glossary.json` rather than retyped here, and the tooltip gets a footer
   * linking to the definition (working-conventions §44). Prefer this over a
   * literal `tooltip` string whenever the label names a term of art — a stat
   * label almost always does.
   */
  term?: string;
  /** `true` shows a bare info icon; a string or node additionally shows it as a hover tooltip explaining the field. */
  tooltip?: boolean | React.ReactNode;
  sx?: object;
  spacing?: boolean;
  /** Label→value gap (theme units). Default 2 (8px). Bump to 3 for dense
   * avatar-block values (payment/address/user previews) so they don't read
   * cramped under the label. */
  valueSpacing?: number;
}) => {
  const theme = useTheme();
  const hasTooltip = Boolean(term) || Boolean(tooltip);
  const infoIcon = hasTooltip ? (
    // `InfoGlyph`, not a local size: this was a 16px `InfoIcon` while `InfoTooltip`'s own
    // glyph was 14 and `BillingOverview` drew an 18, and all three render on the Billing
    // page at once (VRG-437). Drawn here rather than delegated because the label and the
    // glyph are one hover target, which is the case `InfoGlyph` is exported for.
    //
    // The wrapper carries the positioning the glyph itself cannot: the 2px offset is an
    // inline style on purpose, since the parent Stack's own `spacing` emits a `margin: 0`
    // reset on every direct child at a specificity an `sx` selector here cannot beat.
    <span
      style={{
        display: "inline-flex",
        cursor: "pointer",
        flexShrink: 0,
        position: "relative",
        top: 2,
      }}
    >
      <InfoGlyph color={theme.palette.grey[600]} />
    </span>
  ) : null;
  /**
   * The emptiness decision lives HERE, not at the call sites.
   *
   * Every caller used to write its own `?? '—'`, and that is exactly how the
   * Billing "Next invoice date" tile came to render EMPTY while its three
   * neighbours rendered a dash: one site forgot. An N-site convention that any
   * one site can silently drop is a latent bug, not a convention — so a caller
   * that forgets now gets the right thing by construction, and the per-site
   * fallbacks are redundant.
   *
   * The order of these branches is load-bearing:
   *
   * 1. `undefined` is LOADING, not empty. It is the caller's "still fetching"
   *    signal (`upcomingInvoiceLoading ? undefined : value`), so it is tested
   *    FIRST and yields a skeleton. A skeleton and a dash are different states
   *    and must never collapse into each other (working-conventions §19): the
   *    skeleton says "a value is coming", the dash says "we looked and there
   *    is none".
   * 2. `null` / `''` / whitespace-only is EMPTY → the dash. `null` previously
   *    rendered nothing at all, which is the blank-cell ambiguity this fixes.
   * 3. `0` and `false` are VALUES, not emptiness — a falsy check would turn
   *    "0 activations" into "no answer", which are different statements. Note
   *    that React renders a bare boolean child as nothing, so a boolean is
   *    stringified; otherwise `false` would produce the very blank this whole
   *    component is now designed to make impossible.
   */
  const renderValue = () => {
    if (value === undefined) {
      return <Skeleton width={150} />;
    }
    if (React.isValidElement(value)) {
      return value;
    }
    const isEmpty =
      value === null || (typeof value === "string" && value.trim() === "");
    const display = isEmpty
      ? EMPTY_VALUE
      : typeof value === "boolean"
        ? String(value)
        : value;
    return (
      <Typography
        variant="body2"
        color="text.primary"
        sx={{ lineHeight: 1.25 }}
      >
        {display}
      </Typography>
    );
  };

  /**
   * The WHOLE label is the hover target, not just the icon.
   *
   * The icon is 16px square, so a tooltip hung off it alone is a target most
   * readers never find — they read the label, do not understand it, and have no
   * reason to suspect the dot beside it answers the question. Wrapping the label
   * and the dot together turns the thing they are already looking at into the
   * affordance. The dot stays because it is what SAYS an explanation exists;
   * `width: fit-content` keeps the target on the words rather than the whole
   * column, so hovering empty space to the right does nothing.
   */
  const labelRow = (
    <Stack
      direction="row"
      spacing={1.5}
      // Top-aligned, not centred: a label that wraps to two or three lines would
      // otherwise float its dot in the middle of the block, detached from the
      // first line the eye lands on. The 2px nudge keeps a single-line label's
      // dot optically centred against it.
      alignItems="flex-start"
      sx={{
        width: "fit-content",
        ...(hasTooltip ? { cursor: "help" } : null),
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      {infoIcon}
    </Stack>
  );

  return (
    <Stack
      spacing={valueSpacing}
      orientation="vertical"
      sx={{
        px: spacing ? 6 : 0,
        py: 4,
        width: "100%",
        minHeight: 84,
        alignSelf: "baseline",
        ...sx,
      }}
    >
      {hasTooltip ? (
        // The house tooltip, so this stat reads the same as every other explained
        // thing in the app and a `term` resolves against the glossary (§44).
        <InfoTooltip
          term={term}
          text={typeof tooltip === "boolean" ? undefined : tooltip}
        >
          {labelRow}
        </InfoTooltip>
      ) : (
        labelRow
      )}
      {renderValue()}
    </Stack>
  );
};
